import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner, Badge, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay,
    faCog,
    faDatabase,
    faCalendarAlt,
    faSync,
    faCheckCircle,
    faExclamationTriangle,
    faRocket
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { db } from 'config/firebase.js';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';

const CLOUD_FUNCTIONS_URL =
    process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

const PRODUCTS_PATH = 'productCatalog/topicz/products';
const SETTINGS_PATH = 'productCatalog/topicz/settings/amazonDiscovery';

/**
 * DiscoverySettingsTab - Configure and trigger background ASIN discovery
 * 
 * Features:
 * - View discovery statistics
 * - Trigger manual full catalog scan
 * - Configure auto-discovery settings
 */
const DiscoverySettingsTab = () => {
    // Stats
    const [stats, setStats] = useState({
        totalProducts: 0,
        withBarcodes: 0,
        needsScan: 0,
        lastScanDate: null,
        loading: true
    });

    // Settings
    const [settings, setSettings] = useState({
        autoDiscoveryEnabled: true,
        rescanIntervalDays: 30,
        excludeDiscStatus: true
    });

    // Discovery state
    const [isScanning, setIsScanning] = useState(false);
    const [scanProgress, setScanProgress] = useState(null);

    // Load stats and settings
    useEffect(() => {
        const loadStats = async () => {
            try {
                // FIRST: Load settings so we can use excludeDiscStatus for filtering
                let excludeDiscStatus = true; // Default
                let rescanIntervalDays = 30;
                let autoDiscoveryEnabled = true;
                let lastScanDate = null;

                const settingsDoc = await getDoc(doc(db, SETTINGS_PATH));
                if (settingsDoc.exists()) {
                    const data = settingsDoc.data();
                    excludeDiscStatus = data.excludeDiscStatus ?? true;
                    rescanIntervalDays = data.rescanIntervalDays ?? 30;
                    autoDiscoveryEnabled = data.autoDiscoveryEnabled ?? true;
                    lastScanDate = data.lastFullScan?.toDate?.() || null;
                }

                // Update settings state
                setSettings({
                    autoDiscoveryEnabled,
                    rescanIntervalDays,
                    excludeDiscStatus
                });

                // THEN: Get all products and calculate stats using loaded settings
                const productsQuery = query(collection(db, PRODUCTS_PATH));
                const productsSnapshot = await getDocs(productsQuery);

                let totalProducts = 0;
                let withBarcodes = 0;
                let needsScan = 0;
                const scanCutoffDate = new Date();
                scanCutoffDate.setDate(scanCutoffDate.getDate() - rescanIntervalDays);

                productsSnapshot.docs.forEach(docSnap => {
                    const product = docSnap.data();
                    totalProducts++;

                    // Has valid barcode
                    const hasBarcode = product.barcodes?.unit || product.barcodes?.stock;
                    if (hasBarcode) {
                        withBarcodes++;

                        // Check DISC status based on setting (not hardcoded)
                        const passesDiscFilter = !excludeDiscStatus || product.status !== 'DISC';

                        if (passesDiscFilter) {
                            const lastScanned = product.amazonDiscovery?.lastScannedAt?.toDate?.();
                            if (!lastScanned || lastScanned < scanCutoffDate) {
                                needsScan++;
                            }
                        }
                    }
                });

                setStats({
                    totalProducts,
                    withBarcodes,
                    needsScan,
                    lastScanDate,
                    loading: false
                });
            } catch (error) {
                console.error('Error loading stats:', error);
                setStats(prev => ({ ...prev, loading: false }));
            }
        };

        loadStats();
    }, []);

    // Save settings and refresh stats
    const handleSaveSettings = async () => {
        try {
            await setDoc(doc(db, SETTINGS_PATH), {
                ...settings,
                updatedAt: new Date()
            }, { merge: true });
            toast.success('Settings saved - refreshing stats...', { theme: 'colored' });

            // Reload the page to refresh stats with new settings
            setTimeout(() => {
                window.location.reload();
            }, 500);
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        }
    };

    // Trigger manual discovery
    const handleStartDiscovery = async () => {
        setIsScanning(true);
        setScanProgress({ processed: 0, total: stats.needsScan, status: 'Starting...' });

        try {
            // Get products that need scanning
            const productsQuery = query(collection(db, PRODUCTS_PATH));
            const productsSnapshot = await getDocs(productsQuery);

            const scanCutoffDate = new Date();
            scanCutoffDate.setDate(scanCutoffDate.getDate() - settings.rescanIntervalDays);

            const skusToScan = [];
            productsSnapshot.docs.forEach(docSnap => {
                const product = docSnap.data();
                const hasBarcode = product.barcodes?.unit || product.barcodes?.stock;

                // Check DISC status based on setting (not hardcoded)
                const passesDiscFilter = !settings.excludeDiscStatus || product.status !== 'DISC';

                if (hasBarcode && passesDiscFilter) {
                    const lastScanned = product.amazonDiscovery?.lastScannedAt?.toDate?.();
                    if (!lastScanned || lastScanned < scanCutoffDate) {
                        skusToScan.push(product.sku);
                    }
                }
            });

            if (skusToScan.length === 0) {
                toast.info('No products need scanning');
                setIsScanning(false);
                setScanProgress(null);
                return;
            }

            // Process in batches
            const batchSize = 50;
            let processed = 0;

            for (let i = 0; i < skusToScan.length; i += batchSize) {
                const batch = skusToScan.slice(i, i + batchSize);

                setScanProgress({
                    processed,
                    total: skusToScan.length,
                    status: `Processing ${processed + 1}-${Math.min(processed + batchSize, skusToScan.length)}...`
                });

                try {
                    const response = await fetch(`${CLOUD_FUNCTIONS_URL}/asinDiscover`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            skus: batch,
                            options: { force: false }
                        })
                    });

                    if (!response.ok) {
                        console.error('Batch failed:', await response.json());
                    }
                } catch (error) {
                    console.error('Batch error:', error);
                }

                processed += batch.length;
            }

            // Update last scan date
            await setDoc(doc(db, SETTINGS_PATH), {
                lastFullScan: new Date()
            }, { merge: true });

            toast.success(`Discovery complete! Processed ${processed} products`, { theme: 'colored' });
            setScanProgress({ processed, total: skusToScan.length, status: 'Complete!' });

        } catch (error) {
            console.error('Discovery error:', error);
            toast.error('Discovery failed: ' + error.message);
        } finally {
            setIsScanning(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Never';
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="p-3">
            {/* Stats Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <div className="fs-3 fw-bold text-primary">
                                {stats.loading ? <Spinner size="sm" /> : stats.totalProducts}
                            </div>
                            <small className="text-muted">Total Products</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <div className="fs-3 fw-bold text-success">
                                {stats.loading ? <Spinner size="sm" /> : stats.withBarcodes}
                            </div>
                            <small className="text-muted">With Barcodes</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center border-warning">
                        <Card.Body>
                            <div className="fs-3 fw-bold text-warning">
                                {stats.loading ? <Spinner size="sm" /> : stats.needsScan}
                            </div>
                            <small className="text-muted">Need Scanning</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <div className="small text-muted mb-1">Last Full Scan</div>
                            <div className="fw-bold">
                                {stats.loading ? <Spinner size="sm" /> : formatDate(stats.lastScanDate)}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                {/* Manual Discovery */}
                <Col md={6}>
                    <Card className="mb-3">
                        <Card.Header>
                            <FontAwesomeIcon icon={faRocket} className="me-2" />
                            <strong>Run Discovery Scan</strong>
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted small mb-3">
                                Scan products with barcodes to find Amazon ASIN matches.
                                Products with DISC status are automatically excluded.
                                Products scanned within the last {settings.rescanIntervalDays} days are skipped.
                            </p>

                            {scanProgress && (
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between small mb-1">
                                        <span>{scanProgress.status}</span>
                                        <span>{scanProgress.processed} / {scanProgress.total}</span>
                                    </div>
                                    <ProgressBar
                                        now={(scanProgress.processed / scanProgress.total) * 100}
                                        variant={scanProgress.processed === scanProgress.total ? 'success' : 'primary'}
                                        animated={isScanning}
                                    />
                                </div>
                            )}

                            <Button
                                variant="primary"
                                onClick={handleStartDiscovery}
                                disabled={isScanning || stats.needsScan === 0}
                            >
                                {isScanning ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Scanning...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faPlay} className="me-2" />
                                        Start Discovery ({stats.needsScan} products)
                                    </>
                                )}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Settings */}
                <Col md={6}>
                    <Card className="mb-3">
                        <Card.Header>
                            <FontAwesomeIcon icon={faCog} className="me-2" />
                            <strong>Discovery Settings</strong>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="autoDiscovery"
                                    label="Enable automatic nightly discovery"
                                    checked={settings.autoDiscoveryEnabled}
                                    onChange={(e) => setSettings(prev => ({
                                        ...prev,
                                        autoDiscoveryEnabled: e.target.checked
                                    }))}
                                />
                                <Form.Text className="text-muted">
                                    When enabled, new products are automatically scanned each night
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Re-scan interval (days)</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={settings.rescanIntervalDays}
                                    onChange={(e) => setSettings(prev => ({
                                        ...prev,
                                        rescanIntervalDays: parseInt(e.target.value) || 30
                                    }))}
                                    min={7}
                                    max={90}
                                />
                                <Form.Text className="text-muted">
                                    Products will be re-scanned if not updated within this period
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="excludeDisc"
                                    label="Exclude discontinued (DISC) products"
                                    checked={settings.excludeDiscStatus}
                                    onChange={(e) => setSettings(prev => ({
                                        ...prev,
                                        excludeDiscStatus: e.target.checked
                                    }))}
                                />
                            </Form.Group>

                            <Button variant="success" onClick={handleSaveSettings}>
                                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                Save Settings
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Info */}
            <Alert variant="info">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <strong>How Discovery Works:</strong> Products with valid barcodes (UPC/EAN) are searched against
                Amazon's catalog. Matches are scored based on barcode match, title similarity, and brand alignment.
                High-confidence matches appear on the Opportunity Board for your review.
            </Alert>
        </div>
    );
};

export default DiscoverySettingsTab;
